/**
 * Authentication context for Crux RE Platform.
 *
 * Provides:
 * - Login/logout functionality
 * - Token management (stored in memory for security)
 * - Current user state
 * - Token refresh handling
 */

import React, { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import api from '../services/api';

// Types
interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'admin' | 'investor';
  is_active: boolean;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
}

interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: User;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Token storage (in memory for security - not localStorage)
let accessToken: string | null = null;
let refreshTokenValue: string | null = null;

// Export function to get current access token (for API interceptor)
export const getAccessToken = () => accessToken;

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      // Try to get user info if we have a stored refresh token
      // For now, just mark as not loading
      setIsLoading(false);
    };
    initAuth();
  }, []);

  // Login function
  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await api.post<LoginResponse>('/auth/login', {
        email,
        password,
      });

      const { access_token, refresh_token, user: userData } = response.data;

      // Store tokens in memory
      accessToken = access_token;
      refreshTokenValue = refresh_token;

      // Set user state
      setUser(userData);

      // Set authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    } catch (error) {
      // Clear any partial state
      accessToken = null;
      refreshTokenValue = null;
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
      throw error;
    }
  }, []);

  // Logout function
  const logout = useCallback(async () => {
    try {
      // Call logout endpoint (revokes token server-side)
      if (accessToken) {
        await api.post('/auth/logout');
      }
    } catch (error) {
      // Ignore errors during logout
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      accessToken = null;
      refreshTokenValue = null;
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
    }
  }, []);

  // Refresh token function
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (!refreshTokenValue) {
      return false;
    }

    try {
      const response = await api.post<AuthTokens>('/auth/refresh', {
        refresh_token: refreshTokenValue,
      });

      const { access_token, refresh_token: newRefresh } = response.data;

      // Update tokens
      accessToken = access_token;
      refreshTokenValue = newRefresh;

      // Update authorization header
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      return true;
    } catch (error) {
      // Refresh failed, clear everything
      accessToken = null;
      refreshTokenValue = null;
      setUser(null);
      delete api.defaults.headers.common['Authorization'];
      return false;
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    refreshToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Setup axios interceptor for token refresh
export const setupAuthInterceptor = (refreshFn: () => Promise<boolean>) => {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      // If 401 and we haven't already tried to refresh
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        const success = await refreshFn();
        if (success) {
          // Retry the original request with new token
          originalRequest.headers['Authorization'] = `Bearer ${accessToken}`;
          return api(originalRequest);
        }
      }

      return Promise.reject(error);
    }
  );
};

export default AuthContext;
